import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Brain, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { login, signup } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let u;
      if (mode === "signup") {
        u = await signup(name.trim(), email.trim().toLowerCase(), password);
        toast.success("Welcome to BharatIQ");
      } else {
        u = await login(email.trim().toLowerCase(), password);
        toast.success("Welcome back");
      }
      if (u && u.role === "admin") {
        navigate("/admin");
      } else if (mode === "signup") {
        navigate("/onboarding");
      } else {
        // Check if user has a completed session already — if so, go to dashboard
        try {
          const apiMod = await import("../lib/api");
          const [sr, me] = await Promise.all([apiMod.default.get("/me/sessions"), apiMod.default.get("/auth/me")]);
          const done = (sr.data || []).some((s) => s.status === "completed");
          if (done) navigate("/dashboard");
          else navigate(me.data?.age_group ? "/test" : "/onboarding");
        } catch {
          navigate("/test");
        }
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Something went wrong";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail.map((d) => d.msg || d.message || JSON.stringify(d)).join(", ");
      } else if (detail && typeof detail === "object") {
        msg = detail.msg || JSON.stringify(detail);
      } else if (err.message) msg = err.message;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background brand-gradient-bg grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-15"
             style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <Link to="/" className="flex items-center gap-2.5 relative" data-testid="auth-logo">
          <div className="w-9 h-9 rounded-md bg-white/15 grid place-items-center">
            <Brain className="w-5 h-5" />
          </div>
          <span className="font-display text-xl font-extrabold">BharatIQ</span>
        </Link>

        <div className="relative">
          <h2 className="font-display text-4xl xl:text-5xl font-extrabold tracking-tight leading-tight">
            Your mind has patterns. <br />We decode them.
          </h2>
          <p className="mt-6 text-white/70 max-w-md text-base leading-relaxed">
            BharatIQ combines the Big Five personality framework with Holland's RIASEC career
            model — then narrates your results into a clear plan for India's job market.
          </p>
          <div className="mt-8 flex items-center gap-3 text-sm text-white/70">
            <ShieldCheck className="w-4 h-4" /> Secure payments · No subscription
          </div>
        </div>

        <div className="relative text-xs text-white/40">© 2026 BharatIQ</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Brain className="w-4 h-4" />
            </div>
            <span className="font-display text-lg font-extrabold">BharatIQ</span>
          </Link>

          <div className="label-eyebrow">{mode === "signup" ? "New account" : "Welcome back"}</div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight mt-2">
            {mode === "signup" ? "Start your assessment" : "Log in to BharatIQ"}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {mode === "signup" ? "Takes 15 minutes. No quiz vibes." : "Resume your career intelligence."}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "signup" && (
              <div>
                <label className="text-xs font-medium text-foreground/80 block mb-1.5">Full name</label>
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Aarav Sharma"
                  className="w-full px-4 py-3 rounded-md border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-foreground/80 block mb-1.5">Email</label>
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-md border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-foreground/80 block mb-1.5">Password</label>
              <input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                placeholder="••••••••"
                className="w-full px-4 py-3 rounded-md border border-input bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="auth-submit-btn"
              className="btn-primary w-full py-3 rounded-md font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? "Please wait…" : (mode === "signup" ? "Create account" : "Log in")}
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground">
            {mode === "signup" ? (
              <>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  data-testid="auth-switch-login"
                  className="text-primary font-medium hover-underline"
                >
                  Log in
                </button>
              </>
            ) : (
              <>
                New here?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  data-testid="auth-switch-signup"
                  className="text-primary font-medium hover-underline"
                >
                  Create an account
                </button>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
